import { Crown } from 'lucide-react';
import { CoinIcon } from '@/app/components/SvgIcons';
import { CommunityRaffle } from '@/types/raffle';
import { formatUsd, PRIZE_TYPE_LABELS, STATUS_STYLES } from '@/app/raffles/utils';

/** Kick Points amount with the yellow coin — same pairing the wallet uses. */
export function KickAmount({ value, size = 14, className = '' }: { value: number; size?: number; className?: string }) {
    return (
        <span className={`inline-flex items-center gap-1 tabular-nums ${className}`}>
            {value.toLocaleString()}
            <CoinIcon size={size} />
        </span>
    );
}

/** Small mono tag, same as the "Featured Offer" tag on partner cards. */
export function StatusTag({ raffle }: { raffle: CommunityRaffle }) {
    const s = STATUS_STYLES[raffle.status];
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider ${s.className}`}>
            {s.dot && <span className={`w-1.5 h-1.5 rounded-full ${s.dot} animate-pulse`} />}
            {s.label}
        </span>
    );
}

/** Prize amount inline: coins for Kick Points, dollars for a Stake tip. */
export function PrizeAmount({ raffle, size }: { raffle: CommunityRaffle; size: number }) {
    return raffle.prize_type === 'usd'
        ? <span className="tabular-nums">{formatUsd(raffle.prize_pool)}</span>
        : <KickAmount value={raffle.prize_pool} size={size} className="gap-2" />;
}

export function PrizeTypeTag({ raffle }: { raffle: CommunityRaffle }) {
    return (
        <span className="inline-flex items-center rounded-lg bg-gray-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-gray-300">
            {PRIZE_TYPE_LABELS[raffle.prize_type]}
        </span>
    );
}

/** Thin time-elapsed bar for the entry window, along the top edge of a live row. */
export function ProgressBar({ percent }: { percent: number }) {
    return (
        <div className="h-0.5 w-full overflow-hidden">
            <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-green-400 transition-[width] duration-1000 ease-linear"
                style={{ width: `${percent}%` }}
            />
        </div>
    );
}

/** Mono caption above a value — same as "Use Promo Code" on partner cards. */
export function Label({ children }: { children: React.ReactNode }) {
    return <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">{children}</span>;
}

export function WinnerRow({ raffle, viewerId }: { raffle: CommunityRaffle; viewerId?: number }) {
    const winner = raffle.winner;
    if (!winner) {
        return <p className="text-xs font-mono text-gray-400">Nobody entered — no winner drawn.</p>;
    }
    const isYou = winner.user_id === viewerId;
    return (
        <div className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-500/5 px-4 py-3">
            {winner.avatar ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={winner.avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
                <span className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                    <Crown className="w-4 h-4 text-amber-300" />
                </span>
            )}
            <div className="min-w-0 flex-1">
                <div className={`font-mono font-bold truncate ${isYou ? 'text-green-400' : 'text-white'}`}>
                    {winner.username ?? 'Unknown'}{isYou && ' (you)'}
                </div>
                <div className="text-[11px] font-mono text-gray-400">
                    Entered with {winner.tickets.toLocaleString()} {winner.tickets === 1 ? 'ticket' : 'tickets'}
                </div>
            </div>
            <Crown className="w-5 h-5 text-amber-300 shrink-0" />
        </div>
    );
}
